import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';
import { 
  Plus, Search, Edit2, Trash2, Filter, BookOpen, 
  Layers, Calendar, Type, Tag, Settings, ChevronLeft, ChevronRight, Clock, School,
  ArrowLeft
} from 'lucide-react';
import api from '../components/api';
import Modal from '../components/Modal';
import FormInput from '../components/FormInput';
import ConfirmationModal from '../components/ConfirmationModal';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';

// Composant pour les onglets
const TabButton = ({ isActive, onClick, icon: Icon, children }) => (
  <button
    onClick={onClick}
    className={`
      flex-1 px-4 py-4 text-sm font-medium relative transition-all duration-300 ease-in-out
      ${isActive 
        ? 'text-[#202654] bg-white' 
        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
      }
    `}
  >
    <div className="flex items-center justify-center space-x-2">
      <Icon className={`h-5 w-5 transition-colors duration-300 ${isActive ? 'text-[#202654]' : 'text-gray-400'}`} />
      <span>{children}</span>
    </div>
    {isActive && (
      <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#202654] transform scale-x-100 transition-transform duration-300" />
    )}
  </button>
);

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
      <div className="flex justify-between flex-1 sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
            currentPage === 1
              ? 'text-gray-400 bg-gray-100'
              : 'text-gray-700 bg-white hover:bg-gray-50'
          } border border-gray-300`}
        >
          Précédent
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
            currentPage === totalPages
              ? 'text-gray-400 bg-gray-100'
              : 'text-gray-700 bg-white hover:bg-gray-50'
          } border border-gray-300`}
        >
          Suivant
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Page <span className="font-medium">{currentPage}</span> sur{' '}
            <span className="font-medium">{totalPages}</span>
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border ${
                currentPage === 1
                  ? 'text-gray-400 bg-gray-100'
                  : 'text-gray-500 bg-white hover:bg-gray-50'
              } border-gray-300`}
            >
              <span className="sr-only">Précédent</span>
              <ChevronLeft className="h-5 w-5" />
            </button>
            {pages.map(page => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`relative inline-flex items-center px-4 py-2 border ${
                  page === currentPage
                    ? 'z-10 bg-[#202654] border-[#202654] text-white'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border ${
                currentPage === totalPages
                  ? 'text-gray-400 bg-gray-100'
                  : 'text-gray-500 bg-white hover:bg-gray-50'
              } border-gray-300`}
            >
              <span className="sr-only">Suivant</span>
              <ChevronRight className="h-5 w-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

// Ajout des styles personnalisés pour react-select
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    background: '#fff',
    borderColor: state.isFocused ? '#202654' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 1px #202654' : 'none',
    '&:hover': {
      borderColor: '#202654'
    }
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected 
      ? '#202654' 
      : state.isFocused 
        ? '#f3f4f6' 
        : 'white',
    color: state.isSelected ? 'white' : '#374151',
    '&:active': {
      backgroundColor: '#202654'
    }
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#EEF2FF',
    borderRadius: '4px'
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#4F46E5',
    fontWeight: '500',
    fontSize: '0.875rem'
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#4F46E5',
    '&:hover': {
      backgroundColor: '#E0E7FF',
      color: '#4338CA'
    }
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9CA3AF'
  }),
  loadingIndicator: (base) => ({
    ...base,
    color: '#202654'
  })
};

const UniteEnseignementPage = () => {
  const navigate = useNavigate();
  // État pour les onglets
  const [activeTab, setActiveTab] = useState('unites');

  // États existants pour les unités d'enseignement
  const [unites, setUnites] = useState([]);
  const [modules, setModules] = useState([]);
  const [semestres, setSemestres] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUnite, setSelectedUnite] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    classe: '',
    semestre: '',
    type: ''
  });
  const [formData, setFormData] = useState({
    classe: '',
    semestre: '',
    type_unite: '',
    modules: []
  });
  const [formErrors, setFormErrors] = useState({});

  // État pour les modules disponibles pour la classe sélectionnée
  const [availableModules, setAvailableModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  // Nouveaux états pour la gestion des types d'unités
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [searchTermType, setSearchTermType] = useState('');
  const [formDataType, setFormDataType] = useState({
    libelle: ''
  });
  const [formErrorsType, setFormErrorsType] = useState({});
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Nouveaux états pour la pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    count: 0
  });
  
  const [typePagination, setTypePagination] = useState({
    currentPage: 1,
    totalPages: 1,
    count: 0
  });

  const typeOptions = [
    { value: 'UE', label: 'Unité d\'Enseignement (UE)' },
    { value: 'ECUE', label: 'Élément Constitutif d\'UE (ECUE)' }
  ];

  useEffect(() => {
    if (activeTab === 'unites') {
      fetchInitialData();
      fetchTypes();
    }
  }, [activeTab]);

  const fetchInitialData = async (page = 1) => {
    try {
      setLoading(true);
      const [unitesResponse, modulesResponse, semestresResponse, classesResponse] = await Promise.all([
        api.get(`/matiere/liste-unites/?page=${page}`),
        api.get('/matiere/modules/'),
        api.get('/emplois/semestres/actifs/'),
        api.get('/classe/get-all-classe/')
      ]);
      
      setUnites(unitesResponse.data.results);
      setPagination({
        currentPage: unitesResponse.data.current_page,
        totalPages: unitesResponse.data.total_pages,
        count: unitesResponse.data.count
      });
      
      setModules(modulesResponse.data);
      setSemestres(semestresResponse.data);
      setClasses(classesResponse.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des données');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined
      });
    }
  };

  const handleModulesChange = (selectedOptions) => {
    const moduleIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFormData({ ...formData, modules: moduleIds });
    setSelectedModules(selectedOptions || []);
    
    // Clear error when enough modules are selected
    if (formErrors.modules && selectedOptions && selectedOptions.length >= 2) {
      setFormErrors({
        ...formErrors,
        modules: undefined
      });
    } else if (selectedOptions && selectedOptions.length < 2) {
      setFormErrors({
        ...formErrors,
        modules: 'Une unité d\'enseignement doit contenir au moins 2 modules'
      });
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const handleOpenModal = (unite = null) => {
    if (unite) {
      const selectedModuleOptions = (unite.modules || []).map(module => ({
        value: module.id,
        label: module.module?.libelle || 'Module sans nom'
      }));
      
      setFormData({
        classe: unite.classe?.id || '',
        semestre: unite.semestre?.id || '',
        type_unite: unite.type_unite?.id || '',
        modules: unite.modules?.map(m => m.id) || []
      });
      setSelectedModules(selectedModuleOptions);
      setSelectedUnite(unite);
    } else {
      setFormData({
        classe: '',
        semestre: '',
        type_unite: '',
        modules: []
      });
      setSelectedModules([]);
      setSelectedUnite(null);
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.classe) {
      errors.classe = 'La classe est requise';
    }
    
    if (!formData.semestre) {
      errors.semestre = 'Le semestre est requis';
    }
    
    if (!formData.type_unite) {
      errors.type_unite = 'Le type d\'unité est requis';
    }
    
    if (formData.modules.length === 0) {
      errors.modules = 'Au moins un module doit être sélectionné';
    } else if (formData.modules.length < 2) {
      errors.modules = 'Une unité d\'enseignement doit contenir au moins 2 modules';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      if (selectedUnite) {
        await api.put(`/matiere/details-unites/${selectedUnite.id}/`, formData);
        toast.success('Unité d\'enseignement modifiée avec succès');
      } else {
        await api.post('/matiere/liste-unites/', formData);
        toast.success('Unité d\'enseignement créée avec succès');
      }
      setIsModalOpen(false);
      fetchInitialData();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      if (error.response?.data) {
        // Transform API validation errors to match our form
        const apiErrors = {};
        Object.entries(error.response.data).forEach(([field, messages]) => {
          apiErrors[field] = Array.isArray(messages) ? messages[0] : messages;
        });
        setFormErrors(apiErrors);
        
        // Show error messages in toasts
        if (typeof error.response.data === 'object') {
          Object.values(error.response.data).forEach(messages => {
            if (Array.isArray(messages)) {
              messages.forEach(message => toast.error(message));
            } else if (typeof messages === 'string') {
              toast.error(messages);
            }
          });
        } else {
          toast.error('Erreur lors de la sauvegarde de l\'unité d\'enseignement');
        }
      } else {
        toast.error('Erreur lors de la sauvegarde de l\'unité d\'enseignement');
      }
    }
  };

  const handleDelete = async (uniteId) => {
    try {
      await api.delete(`/matiere/details-unites/${uniteId}/`);
      toast.success('Unité d\'enseignement supprimée avec succès');
      fetchInitialData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          Object.values(error.response.data).forEach(messages => {
            if (Array.isArray(messages)) {
              messages.forEach(message => toast.error(message));
            } else if (typeof messages === 'string') {
              toast.error(messages);
            }
          });
        } else {
          toast.error('Erreur lors de la suppression de l\'unité d\'enseignement');
        }
      } else {
        toast.error('Erreur lors de la suppression de l\'unité d\'enseignement');
      }
    }
  };

  const filteredUnites = unites.filter(unite => {
    if (!unite) return false;
    
    const matchesClasseFilter = !filters.classe || (unite.classe && unite.classe.id.toString() === filters.classe);
    const matchesSemestreFilter = !filters.semestre || (unite.semestre && unite.semestre.id.toString() === filters.semestre);
    const matchesTypeFilter = !filters.type || (unite.type_unite && unite.type_unite.id.toString() === filters.type);
    
    // Recherche dans les modules associés
    const matchesSearchTerm = searchTerm ? unite.modules?.some(module => 
      module.module?.libelle?.toLowerCase().includes(searchTerm.toLowerCase())
    ) : true;
    
    return matchesClasseFilter && matchesSemestreFilter && matchesTypeFilter && matchesSearchTerm;
  });

  const resetFilters = () => {
    setFilters({
      classe: '',
      semestre: '',
      type: ''
    });
  };

  const renderModalFooter = () => (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={() => setIsModalOpen(false)}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        Annuler
      </button>
      <button
        type="submit"
        onClick={handleSubmit}
        className="px-4 py-2 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        {selectedUnite ? 'Modifier' : 'Créer'}
      </button>
    </div>
  );

  // Nouvelles fonctions pour la gestion des types d'unités
  const fetchTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await api.get('/matiere/type-unites/');
      setTypes(response.data.results || response.data);
      setLoadingTypes(false);
    } catch (error) {
      console.error('Erreur lors du chargement des types:', error);
      toast.error('Erreur lors du chargement des types d\'unités');
      setLoadingTypes(false);
    }
  };

  const handleTypeInputChange = (e) => {
    const { name, value } = e.target;
    setFormDataType({ ...formDataType, [name]: value });
    
    if (formErrorsType[name]) {
      setFormErrorsType({
        ...formErrorsType,
        [name]: undefined
      });
    }
  };

  const handleOpenTypeModal = (type = null) => {
    if (type) {
      setFormDataType({
        libelle: type.libelle
      });
      setSelectedType(type);
    } else {
      setFormDataType({
        libelle: ''
      });
      setSelectedType(null);
    }
    setFormErrorsType({});
    setIsTypeModalOpen(true);
  };

  const validateTypeForm = () => {
    const errors = {};
    
    if (!formDataType.libelle.trim()) {
      errors.libelle = 'Le libellé est requis';
    }
    
    setFormErrorsType(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateTypeForm()) return;
    
    try {
      if (selectedType) {
        await api.put(`/matiere/type-unites/${selectedType.id}/`, formDataType);
        toast.success('Type d\'unité modifié avec succès');
      } else {
        await api.post('/matiere/type-unites/', formDataType);
        toast.success('Type d\'unité créé avec succès');
      }
      setIsTypeModalOpen(false);
      fetchTypes();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      if (error.response?.data) {
        const apiErrors = {};
        Object.entries(error.response.data).forEach(([field, messages]) => {
          apiErrors[field] = Array.isArray(messages) ? messages[0] : messages;
        });
        setFormErrorsType(apiErrors);
      } else {
        toast.error('Erreur lors de la sauvegarde du type d\'unité');
      }
    }
  };

  const handleOpenTypeDeleteModal = (type) => {
    setTypeToDelete(type);
    setIsConfirmModalOpen(true);
  };

  const handleTypeDelete = async () => {
    if (!typeToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/matiere/type-unites/${typeToDelete.id}/`);
      toast.success('Type d\'unité supprimé avec succès');
      fetchTypes();
      setIsConfirmModalOpen(false);
      setTypeToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression du type d\'unité');
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredTypes = types.filter(type => {
    if (!type) return false;
    return type.libelle ? type.libelle.toLowerCase().includes(searchTermType.toLowerCase()) : false;
  });

  const renderTypeModalFooter = () => (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={() => setIsTypeModalOpen(false)}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        Annuler
      </button>
      <button
        type="submit"
        onClick={handleTypeSubmit}
        className="px-4 py-2 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        {selectedType ? 'Modifier' : 'Créer'}
      </button>
    </div>
  );

  // Fonction pour charger les modules disponibles pour une classe
  const fetchModulesByClasse = async (classeId) => {
    if (!classeId) {
      setAvailableModules([]);
      return;
    }

    try {
      setLoadingModules(true);
      const response = await api.get(`/matiere/module-classes/${classeId}/modules/`);
      setAvailableModules(response.data);
      setLoadingModules(false);
    } catch (error) {
      console.error('Erreur lors du chargement des modules:', error);
      toast.error('Erreur lors du chargement des modules');
      setLoadingModules(false);
    }
  };

  // Gestionnaire de changement de classe
  const handleClasseChange = (e) => {
    const classeId = e.target.value;
    setFormData({
      ...formData,
      classe: classeId,
      modules: [] // Réinitialiser les modules sélectionnés
    });
    setSelectedModules([]); // Réinitialiser les modules sélectionnés dans react-select
    fetchModulesByClasse(classeId);
  };

  // Gestionnaires de changement de page
  const handlePageChange = (page) => {
    fetchInitialData(page);
  };

  const handleTypePageChange = (page) => {
    fetchTypes(page);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <Layers className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Gestion des Unités d'Enseignement</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les unités d'enseignement et leurs modules associés
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-6">
              <div className="max-w-7xl mx-auto">
                {/* Contenu existant */}
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Liste des unités</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      {activeTab === 'unites' 
                        ? 'Gérez les unités d\'enseignement et leurs modules associés'
                        : 'Gérez les types d\'unités d\'enseignement'}
                    </p>
                  </div>
                  <button
                    onClick={() => activeTab === 'unites' ? handleOpenModal() : handleOpenTypeModal()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    {activeTab === 'unites' ? 'Nouvelle Unité' : 'Nouveau Type'}
                  </button>
                </div>

                {/* Onglets */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                  <nav className="flex">
                    <TabButton
                      isActive={activeTab === 'unites'}
                      onClick={() => setActiveTab('unites')}
                      icon={Layers}
                    >
                      Unités d'enseignement
                    </TabButton>
                    <TabButton
                      isActive={activeTab === 'types'}
                      onClick={() => setActiveTab('types')}
                      icon={Settings}
                    >
                      Types d'unités
                    </TabButton>
                  </nav>
                </div>

                {/* Contenu de l'onglet actif */}
                {activeTab === 'unites' ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200 flex flex-col gap-4">
                      <div className="flex items-center">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            placeholder="Rechercher une unité d'enseignement..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-[#202654] focus:border-[#202654]"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                        
                        <button
                          onClick={() => document.getElementById('filterCollapse').classList.toggle('hidden')}
                          className="ml-3 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
                        >
                          <Filter className="h-4 w-4 mr-2" />
                          Filtres
                        </button>
                      </div>
                      
                      <div id="filterCollapse" className="hidden pt-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <FormInput
                            label="Classe"
                            type="select"
                            name="classe"
                            value={filters.classe}
                            onChange={handleFilterChange}
                            options={[
                              { value: '', label: 'Toutes les classes' },
                              ...classes.map(classe => ({ 
                                value: classe.id, 
                                label: `${classe.libelle} (${classe.niveau?.libelle || 'N/A'} - ${classe.filiere?.libelle || 'N/A'})` 
                              }))
                            ]}
                          />
                          
                          <FormInput
                            label="Semestre"
                            type="select"
                            name="semestre" 
                            value={filters.semestre}
                            onChange={handleFilterChange}
                            options={[
                              { value: '', label: 'Tous les semestres' },
                              ...semestres.map(semestre => ({ value: semestre.id, label: semestre.libelle }))
                            ]}
                          />
                          
                          <FormInput
                            label="Type d'unité"
                            type="select"
                            name="type"
                            value={filters.type}
                            onChange={handleFilterChange}
                            options={[
                              { value: '', label: 'Tous les types' },
                              ...types.map(type => ({ value: type.id, label: type.libelle }))
                            ]}
                          />
                        </div>
                        
                        <div className="flex justify-end mt-4">
                          <button
                            onClick={resetFilters}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
                          >
                            Réinitialiser les filtres
                          </button>
                        </div>
                      </div>
                    </div>

                    {loading ? (
                      <div className="flex justify-center items-center p-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                             
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Classe
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Semestre
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Modules
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Dernière modification
                              </th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUnites.length === 0 ? (
                              <tr>
                                <td colSpan="7" className="px-6 py-12">
                                  <div className="flex flex-col items-center justify-center text-center">
                                    <Layers className="h-12 w-12 text-gray-400 mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">Aucune unité d'enseignement trouvée</h3>
                                    <p className="text-sm text-gray-500 max-w-md">
                                      {searchTerm || filters.classe || filters.semestre || filters.type
                                        ? 'Essayez avec d\'autres critères de recherche ou filtres'
                                        : 'Commencez par créer votre première unité d\'enseignement'}
                                    </p>
                                    {!searchTerm && !filters.classe && !filters.semestre && !filters.type && (
                                      <button
                                        onClick={() => handleOpenModal()}
                                        className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90"
                                      >
                                        <Plus className="h-5 w-5 mr-2" />
                                        Créer une unité d'enseignement
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              filteredUnites.map((unite) => (
                                <tr key={unite.id} className="hover:bg-gray-50 transition-colors">
                                 
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800'
                                    >
                                      <Type className="h-3 w-3 mr-1" />
                                      {unite.type_unite.libelle} 
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {unite.classe ? (
                                      <div className="text-sm text-gray-900">
                                        {unite.classe.libelle} 
                                        <span className="text-xs text-gray-500 block">
                                          {unite.classe.niveau?.libelle} - {unite.classe.filiere?.libelle}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                        Non défini
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {unite.semestre ? (
                                      <div className="flex items-center">
                                        <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                                        <span className="text-sm text-gray-900">{unite.semestre.libelle}</span>
                                      </div>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                        Non défini
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex flex-wrap gap-1">
                                      {(unite.modules || []).length === 0 ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                          Aucun module
                                        </span>
                                      ) : (
                                        (unite.modules || []).slice(0, 2).map(module => (
                                          <span 
                                            key={module.id} 
                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                                            title={module.module?.libelle}
                                          >
                                            <BookOpen className="h-3 w-3 mr-1" />
                                            {module.module?.libelle}
                                          </span>
                                        ))
                                      )}
                                      {(unite.modules || []).length > 2 && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                          +{unite.modules.length - 2}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center text-sm text-gray-500">
                                      <Clock className="h-4 w-4 mr-2" />
                                      {new Date(unite.updated_at).toLocaleDateString()}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                      onClick={() => handleOpenModal(unite)}
                                      className="text-indigo-600 hover:text-indigo-900 mr-4 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                                      title="Modifier"
                                    >
                                      <Edit2 className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(unite.id)}
                                      className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {!loading && filteredUnites.length > 0 && (
                      <Pagination
                        currentPage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                      />
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex items-center">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            placeholder="Rechercher un type d'unité..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-[#202654] focus:border-[#202654]"
                            value={searchTermType}
                            onChange={(e) => setSearchTermType(e.target.value)}
                          />
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {loadingTypes ? (
                      <div className="flex justify-center items-center p-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type d'unité
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date de création
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Dernière modification
                              </th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredTypes.length === 0 ? (
                              <tr>
                                <td colSpan="4" className="px-6 py-12">
                                  <div className="flex flex-col items-center justify-center text-center">
                                    <Tag className="h-12 w-12 text-gray-400 mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun type d'unité trouvé</h3>
                                    <p className="text-sm text-gray-500 max-w-md">
                                      {searchTermType
                                        ? 'Essayez avec un autre terme de recherche'
                                        : 'Commencez par créer votre premier type d\'unité'}
                                    </p>
                                    {!searchTermType && (
                                      <button
                                        onClick={() => handleOpenTypeModal()}
                                        className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90"
                                      >
                                        <Plus className="h-5 w-5 mr-2" />
                                        Créer un type d'unité
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              filteredTypes.map((type) => (
                                <tr key={type.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div className="h-9 w-9 rounded-full bg-[#202654]/10 flex items-center justify-center mr-3">
                                        <Tag className="h-5 w-5 text-[#202654]" />
                                      </div>
                                      <div className="text-sm font-medium text-gray-900">
                                        {type.libelle}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center text-sm text-gray-500">
                                      <Calendar className="h-4 w-4 mr-2" />
                                      {new Date(type.created_at).toLocaleDateString()}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center text-sm text-gray-500">
                                      <Clock className="h-4 w-4 mr-2" />
                                      {new Date(type.updated_at).toLocaleDateString()}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                      onClick={() => handleOpenTypeModal(type)}
                                      className="text-indigo-600 hover:text-indigo-900 mr-4 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                                      title="Modifier"
                                    >
                                      <Edit2 className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={() => handleOpenTypeDeleteModal(type)}
                                      className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {!loadingTypes && filteredTypes.length > 0 && (
                      <Pagination
                        currentPage={typePagination.currentPage}
                        totalPages={typePagination.totalPages}
                        onPageChange={handleTypePageChange}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modals existants */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedUnite ? 'Modifier l\'unité d\'enseignement' : 'Nouvelle unité d\'enseignement'}
          footer={renderModalFooter()}
          size="large"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Type d'unité"
                type="select"
                name="type_unite"
                value={formData.type_unite}
                onChange={handleInputChange}
                options={[
                  { value: '', label: 'Sélectionner un type d\'unité' },
                  ...types.map(type => ({
                    value: type.id,
                    label: type.libelle
                  }))
                ]}
                required
                error={formErrors.type_unite}
                icon={Type}
                isLoading={loadingTypes}
              />
              
              <FormInput
                label="Classe"
                type="select"
                name="classe"
                value={formData.classe}
                onChange={handleClasseChange}
                options={[
                  { value: '', label: 'Sélectionner une classe' },
                  ...classes.map(classe => ({ 
                    value: classe.id, 
                    label: `${classe.libelle || ''} (${classe.niveau?.libelle || ''} - ${classe.filiere?.libelle || ''})`
                  }))
                ]}
                required
                error={formErrors.classe}
                icon={School}
              />
            </div>
            
            <FormInput
              label="Semestre"
              type="select"
              name="semestre"
              value={formData.semestre}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Sélectionner un semestre' },
                ...semestres.map(semestre => ({
                  value: semestre.id,
                  label: semestre.libelle
                }))
              ]}
              required
              error={formErrors.semestre}
              icon={Calendar}
            />
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Modules associés
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <BookOpen className="h-5 w-5 text-gray-400" />
                </div>
                <Select
                  isMulti
                  value={selectedModules}
                  onChange={handleModulesChange}
                  options={availableModules.map(moduleClasse => ({
                    value: moduleClasse.id,
                    label: moduleClasse.module.libelle,
                    details: `${moduleClasse.nombre_heure}h - Coef. ${moduleClasse.coeficient}`
                  }))}
                  styles={customSelectStyles}
                  className="pl-10"
                  classNamePrefix="react-select"
                  placeholder={loadingModules ? "Chargement des modules..." : "Sélectionnez au moins 2 modules"}
                  isDisabled={!formData.classe || loadingModules}
                />
              </div>
              {formErrors.modules && (
                <p className="mt-1 text-sm text-red-600">{formErrors.modules}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                {formData.classe 
                  ? "Sélectionnez les modules à associer à cette unité d'enseignement (minimum 2)" 
                  : "Sélectionnez d'abord une classe pour voir les modules disponibles"}
              </p>
            </div>
          </form>
        </Modal>

        <Modal
          isOpen={isTypeModalOpen}
          onClose={() => setIsTypeModalOpen(false)}
          title={selectedType ? 'Modifier le type d\'unité' : 'Nouveau type d\'unité'}
          footer={renderTypeModalFooter()}
        >
          <form onSubmit={handleTypeSubmit} className="space-y-6">
            <FormInput
              label="Libellé du type"
              type="text"
              name="libelle"
              value={formDataType.libelle}
              onChange={handleTypeInputChange}
              placeholder="Entrez le nom du type d'unité"
              required
              error={formErrorsType.libelle}
              icon={Tag}
            />
          </form>
        </Modal>

        <ConfirmationModal 
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleTypeDelete}
          title="Supprimer le type d'unité"
          message={`Êtes-vous sûr de vouloir supprimer le type d'unité "${typeToDelete?.libelle}" ? Cette action est irréversible.`}
          confirmText="Supprimer"
          cancelText="Annuler"
          type="danger"
          isLoading={deleteLoading}
        />
      </div>
    </Layout>
  );
};

export default UniteEnseignementPage;
